import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Autocomplete,
  Box,
  Chip,
  CircularProgress,
  TextField,
  Typography,
} from "@mui/material";

/**
 * Generic async multi-select dropdown
 *
 * Props:
 * - sectionLabel: string (label shown above the field)
 * - inputLabel: string (TextField label/placeholder)
 * - selectedValues: T[] (array of selected values)
 * - onSelectionChange: (values: T[]) => void
 * - fetchOptions: (query: string) => Promise<T[]>
 * - getOptionLabel: (option: T) => string
 * - compareOptions?: (option: T, value: T) => boolean
 * - minCharacters?: number (default: 0)
 * - debounceDelay?: number (default: 300)
 * - clearOptionsOnEmptyQuery?: boolean (default: false)
 * - disabled?: boolean
 * - sx?: object (MUI styling prop for Box)
 */
export function GenericMultiSelect({
  sectionLabel,
  inputLabel = "Search & select…",
  selectedValues,
  onSelectionChange,
  fetchOptions,
  getOptionLabel,
  compareOptions,
  minCharacters = 0,
  debounceDelay = 300,
  clearOptionsOnEmptyQuery = false,
  disabled = false,
  sx,
}) {
  const [searchQuery, setSearchQuery] = useState("");
  const [availableOptions, setAvailableOptions] = useState([]);
  const [loadingOptions, setLoadingOptions] = useState(false);

  const isComponentActiveRef = useRef(true);
  const debounceTimerRef = useRef(null);

  const optionComparator =
    compareOptions ||
    ((a, b) => String(getOptionLabel(a)) === String(getOptionLabel(b)));

  useEffect(() => {
    return () => {
      isComponentActiveRef.current = false;
      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    };
  }, []);

  useEffect(() => {
    if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);

    const shouldFetch =
      (!clearOptionsOnEmptyQuery || searchQuery.length > 0) &&
      searchQuery.length >= minCharacters;

    if (!shouldFetch) {
      setAvailableOptions([]);
      return;
    }

    debounceTimerRef.current = setTimeout(async () => {
      try {
        setLoadingOptions(true);
        const results = await fetchOptions(searchQuery);
        if (!isComponentActiveRef.current) return;
        setAvailableOptions(Array.isArray(results) ? results : []);
      } catch {
        if (!isComponentActiveRef.current) return;
        setAvailableOptions([]);
      } finally {
        if (!isComponentActiveRef.current) return;
        setLoadingOptions(false);
      }
    }, debounceDelay);
  }, [
    searchQuery,
    debounceDelay,
    clearOptionsOnEmptyQuery,
    minCharacters,
    fetchOptions,
  ]);

  // Keep selected items visible even if not in the current fetched list
  const mergedOptions = useMemo(() => {
    const uniqueByLabel = new Map();
    for (const selected of selectedValues || []) {
      uniqueByLabel.set(String(getOptionLabel(selected)), selected);
    }
    for (const option of availableOptions || []) {
      uniqueByLabel.set(String(getOptionLabel(option)), option);
    }
    return Array.from(uniqueByLabel.values());
  }, [availableOptions, selectedValues, getOptionLabel]);

  return (
    <Box sx={sx}>
      {sectionLabel && (
        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          {sectionLabel}
        </Typography>
      )}

      <Autocomplete
        multiple
        disabled={disabled}
        options={mergedOptions}
        loading={loadingOptions}
        value={selectedValues}
        onChange={(_, newValues) => onSelectionChange(newValues)}
        onInputChange={(_, newInputValue) => setSearchQuery(newInputValue)}
        isOptionEqualToValue={optionComparator}
        getOptionLabel={getOptionLabel}
        filterSelectedOptions
        disableCloseOnSelect
        renderTags={(selected, getTagProps) =>
          selected.map((option, index) => (
            <Chip
              {...getTagProps({ index })}
              key={`${getOptionLabel(option)}_${index}`}
              label={getOptionLabel(option)}
              sx={{ mb: 0.5 }}
            />
          ))
        }
        renderInput={(params) => (
          <TextField
            {...params}
            label={inputLabel}
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {loadingOptions ? <CircularProgress size={18} /> : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
      />
    </Box>
  );
}



